// Referral programme upsell dialog — when it may appear, and how often.
//
// Mirrors the cooldown shape of PWA_DISMISS_COOLDOWN_MS (constants/pwa.ts) and
// usePackageUpsellPrompt: a localStorage timestamp compared against a fixed
// window. Unlike the package upsell there is no PostHog experiment here — the
// interval is a product decision, not an A/B test. If that changes, swap
// REFERRAL_UPSELL_COOLDOWN_MS for a variant lookup the same way
// usePackageUpsellPrompt does.

export const REFERRAL_UPSELL_LAST_SHOWN_KEY = "hh:referral_upsell_last_shown";

/** Shown once, then not again for 7 days. */
export const REFERRAL_UPSELL_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Delay before the dialog opens. Firing on first paint interrupts a visitor who
 * is still working out what the site is, so it waits until they have settled.
 */
export const REFERRAL_UPSELL_DELAY_MS = 5000;

/**
 * Public surfaces the dialog may appear on: home, the service pages, FAQ, and
 * the dashboard.
 *
 * Allowlist rather than blocklist, so a new route never starts showing a modal
 * by accident. Deliberately excluded:
 *   - /ref/:code   — a referred visitor already got this pitch on the landing page
 *   - /login, /signup, /forgot-password, /reset-password — mid-task, do not interrupt
 *   - /invoice/:token — a customer opening a bill
 *   - the booking forms and everything behind auth — mid-checkout is the worst
 *     possible moment for an upsell
 */
const EXACT_PATHS = ["/", "/faq", "/dashboard"];
const PREFIX_PATHS = ["/services/"];

/** Trailing slashes and casing vary by how the user arrived; normalise both. */
function normalize(pathname: string): string {
  const lower = pathname.toLowerCase();
  return lower.length > 1 && lower.endsWith("/") ? lower.slice(0, -1) : lower;
}

export function isReferralUpsellPath(pathname: string): boolean {
  const path = normalize(pathname);
  if (EXACT_PATHS.includes(path)) return true;
  return PREFIX_PATHS.some((prefix) => path.startsWith(prefix));
}
